import { motion } from 'framer-motion'
import { useState } from 'react'

// Partner-Unternehmen mit Logo-Pfaden
// Logos sind in public/logos/ abgelegt
const partners = [
  { 
    name: 'Vodafone', 
    initials: 'VF', 
    logoPaths: ['/logos/vodafone.png']
  },
  { 
    name: 'Bethmann Bank', 
    initials: 'BB', 
    logoPaths: ['/logos/bethmann-bank.png', '/logos/bethmann.png', '/logos/bethmannbank.png']
  },
  { 
    name: 'Vorwerk', 
    initials: 'VW', 
    logoPaths: ['/logos/vorwerk.png']
  },
  { 
    name: 'SWK', 
    initials: 'SWK', 
    logoPaths: ['/logos/swk_neu.png']
  },
  { 
    name: 'Helios Krefeld', 
    initials: 'HK', 
    logoPaths: ['/logos/helioskrefeld.png']
  },
  { 
    name: 'Helios Duisburg', 
    initials: 'HD', 
    logoPaths: ['/logos/heliosduisburg.png']
  },
  { 
    name: 'Tölke & Fischer Gruppe', 
    initials: 'TF', 
    logoPaths: ['/logos/toelkefischer.png']
  },
  { 
    name: 'ILW Logistics', 
    initials: 'ILW', 
    logoPaths: ['/logos/ilw.png', '/logos/ilw-logistics.png']
  },
  { 
    name: 'Stockmanns', 
    initials: 'ST', 
    logoPaths: ['/logos/stockmanns.png']
  },
  { 
    name: 'NGN Netzgesellschaft', 
    initials: 'NGN', 
    logoPaths: ['/logos/ngn.png']
  },
  { 
    name: 'Fressnapf', 
    initials: 'FN', 
    logoPaths: ['/logos/fressnapf.png']
  },
]

// Logo-Komponente mit Fallback auf Platzhalter
// Versucht mehrere mögliche Logo-Pfade
function PartnerLogo({ name, initials, logoPaths }: { name: string; initials: string; logoPaths: string[] }) {
  const [currentLogoIndex, setCurrentLogoIndex] = useState(0)
  const [imageError, setImageError] = useState(false)
  const [imageLoaded, setImageLoaded] = useState(false)

  const handleImageError = () => {
    if (currentLogoIndex < logoPaths.length - 1) {
      // Versuche nächsten Pfad
      setCurrentLogoIndex(currentLogoIndex + 1)
      setImageLoaded(false)
    } else {
      // Alle Pfade fehlgeschlagen, zeige Platzhalter
      setImageError(true)
    }
  }

  return (
    <div className="flex flex-col items-center justify-center gap-1.5 w-full min-w-0">
      {!imageError ? (
        <>
          <div className="w-20 h-20 md:w-24 md:h-24 flex items-center justify-center flex-shrink-0 relative">
            <img
              src={logoPaths[currentLogoIndex]}
              alt={name}
              className={`max-w-full max-h-full object-contain transition-opacity duration-300 filter brightness-0 invert opacity-70 group-hover:opacity-100 ${
                imageLoaded ? 'opacity-100' : 'opacity-0'
              }`}
              onLoad={() => setImageLoaded(true)}
              onError={handleImageError}
            />
            {!imageLoaded && !imageError && (
              <div className="w-12 h-12 md:w-14 md:h-14 rounded-lg bg-white/10 flex items-center justify-center absolute">
                <span className="text-white/60 font-bold text-base md:text-lg tracking-tight">{initials}</span>
              </div>
            )}
          </div>
          <span className="text-white/40 text-[9px] md:text-[10px] font-medium tracking-wider uppercase text-center leading-tight px-1 break-words max-w-full line-clamp-2 hyphens-auto">
            {name}
          </span>
        </>
      ) : (
        <>
          <div className="w-12 h-12 md:w-14 md:h-14 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
            <span className="text-white/60 font-bold text-base md:text-lg tracking-tight">{initials}</span>
          </div>
          <span className="text-white/40 text-[9px] md:text-[10px] font-medium tracking-wider uppercase text-center leading-tight px-1 break-words max-w-full line-clamp-2 hyphens-auto">
            {name}
          </span>
        </>
      )}
    </div>
  )
}

interface LogoSliderProps {
  title?: string
  subtitle?: string
}

export default function LogoSlider({ 
  title = "Vertrauen von führenden Unternehmen",
  subtitle = "Unsere Partner"
}: LogoSliderProps) {
  // Dupliziere die Logos für nahtlose Animation (3x für flüssige Endlosschleife)
  const duplicatedPartners = [...partners, ...partners, ...partners]

  return (
    <section className="relative py-16 md:py-24 overflow-hidden border-t border-white/5">
      {/* Subtle Background Glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[300px] bg-brand-800/5 rounded-full blur-[100px]" />
      
      <div className="relative z-10">
        {/* Header */}
        <div className="text-center mb-12 px-6">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-brand-400 text-xs uppercase tracking-[0.4em] mb-4"
          >
            {subtitle}
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="font-serif text-2xl md:text-3xl lg:text-4xl text-white font-light"
          >
            {title}
          </motion.h2>
        </div>

        {/* Logo Slider Track */}
        <div className="relative">
          {/* Fade edges */}
          <div className="absolute left-0 top-0 bottom-0 w-24 md:w-40 bg-gradient-to-r from-[#0a0a0a] to-transparent z-10 pointer-events-none" />
          <div className="absolute right-0 top-0 bottom-0 w-24 md:w-40 bg-gradient-to-l from-[#0a0a0a] to-transparent z-10 pointer-events-none" />
          
          {/* Scrolling Container */}
          <div className="overflow-hidden">
            <motion.div 
              className="flex items-center gap-6 md:gap-10"
              animate={{ 
                x: ['0%', '-33.333%']
              }}
              transition={{
                x: {
                  repeat: Infinity,
                  repeatType: "loop",
                  duration: 16,
                  ease: "linear",
                },
              }}
            >
              {duplicatedPartners.map((partner, index) => (
                <motion.div
                  key={`${partner.name}-${index}`}
                  className="flex-shrink-0 group"
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="w-40 md:w-48 h-28 md:h-32 flex items-center justify-center px-4 py-3 rounded-xl bg-white/[0.03] border border-white/5 group-hover:border-brand-800/30 group-hover:bg-white/[0.06] transition-all duration-300 overflow-hidden">
                    <PartnerLogo name={partner.name} initials={partner.initials} logoPaths={partner.logoPaths} />
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>

        {/* Trust indicator */}
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
          className="text-center text-white/30 text-xs uppercase tracking-[0.2em] mt-10"
        >
          200+ erfolgreiche Firmenevents
        </motion.p>
      </div>
    </section>
  )
}

